<script lang="ts" setup>
import { createReusableTemplate, useMediaQuery } from '@vueuse/core'
import { Button } from '@/components/ui/button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Drawer, DrawerContent, DrawerTrigger } from '@/components/ui/drawer'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Checkbox } from '@/components/ui/checkbox'

interface Option {
    label: string,
    value: string | number,
}

const modelValue = defineModel('modelValue')

const props = defineProps({
    label: {
        type: String,
        required: true
    },
    icon: {
        type: String,
    },
    options: {
        type: Array as PropType<Option[]>,
        required: true,
    },
    btn_class: {
        type: String,
        default: null
    },
})

const [UseTemplate, OptionsList] = createReusableTemplate()
const isDesktop = ref(true) // По умолчанию true для SSR
onMounted(() => {
  const mediaQuery = useMediaQuery('(min-width: 768px)')
  watchEffect(() => {
    isDesktop.value = mediaQuery.value
  })
})
const isOpen = ref(false)

function handleChange(option: Option) {
    if(modelValue.value === option.value) {
        modelValue.value = null
        return
    }
    modelValue.value = option.value
}
</script>

<template>
    <div>
        <UseTemplate>
            <Command>
                <CommandInput v-if="options.length > 10 " :placeholder="$t('Filter options...')" />
                <CommandList>
                    <CommandEmpty>{{ $t('No results found') }}</CommandEmpty>
                    <CommandGroup>
                        <CommandItem v-for="option in options" :key="option.value" :value="option.value" class="py-2 hover:bg-stroke">
                            <label>
                                <Checkbox 
                                    :checked="modelValue === option.value"
                                    @update:checked="handleChange(option)"
                                    class="filter__checkbox border-gray-200 border-2 shadow-sm rounded data-[state=checked]:bg-primaryblue-500 data-[state=checked]:border-0 mr-2 ">
                                        <Icon name="ri:check-fill" size="14px" />
                                    </Checkbox>
                                <span :class="{
                                    'font-medium': modelValue === option.value
                                }">{{ option.label }}</span>
                                
                            </label>
                        </CommandItem>
                    </CommandGroup>
                </CommandList>
            </Command>
        </UseTemplate>

        <Popover v-if="isDesktop" v-model:open="isOpen">
            <PopoverTrigger as-child>
                <Button variant="outline" class="w-fit justify-between font-normal text-sub text-sm" :class="props.btn_class ?? 'max-h-[36px] px-2'">
                    <div class="flex">
                        <Icon 
                        v-if="icon" 
                        size="20" 
                        :name="icon" 
                        class="mr-2 rtl:ml-2 font-normal"
                        :class="{ 'text-primaryblue-500': !!modelValue }" />
                        <span>{{ label }}</span>
                        <UiBadge v-if="!!modelValue" class="ml-2 text-[9px] p-0 h-[19px] w-[19px] justify-center leading-none bg-primaryblue-500">{{ 1 }}</UiBadge>
                    </div>
                    <Icon size="18" name="ri:arrow-down-s-line" class="ml-2 rtl:mr-2 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent class="w-[200px] p-0" align="start">
                <OptionsList />
            </PopoverContent>
        </Popover>

        <Drawer v-else :open="isOpen" @update:open="(newOpenValue) => isOpen = newOpenValue">
            <DrawerTrigger as-child>
                <Button variant="outline" class="w-[150px] justify-start">
                    {{ label }}
                </Button>
            </DrawerTrigger>
            <DrawerContent>
                <div class="mt-4 border-t">
                    <OptionsList />
                </div>
            </DrawerContent>
        </Drawer>
    </div>
</template>

<style lang="scss">
.filter__checkbox[data-state="checked"]{
    box-shadow: inset 0 2px 2px rgba(#162664, .32)
}
</style>